// 剣舞の新しい技「遅れの一撃」(2026-10-25・開発版 ?bladeGamma=1)。アルレッキーノ (Lies of P) の、刃が赤く染まってから来る一撃を落とし込んだ。
//   作者「剣舞の技の数は全然増やしていいよ。ひとまずアルレッキーノを剣舞に落とし込む形で作ってもらって、実プレイしながら調整していきたい。」
//   技の組み立て = docs/research/2026-10-25-arlecchino-attack-analysis.md (色が変わる一撃 γ)。
//   流れ: 立ったまま剣2本を縦に見せて赤く染まる (約0.9秒の間) → 振りかぶる → 大きく踏み込んで斬り下ろす (1発目)
//         → 振り抜いたまま 0.38秒おいて、剣先の床が爆ぜる (2発目) → 肩で息の残心 → 立ち姿。
//   弾けない (赤＝避ける)。赤い呪いの纏いは unblockable で render.ts auraStrength が自動で付ける。
//   1発目を避けて下がっただけだと、遅れて爆ぜる2発目に捕まる (剣先の床が届く所)。前か後ろへ大きく抜けるのが正解。
//   絵 = public/sprites/bosses/blade-dancer/m1025_g_{1..4}.png (横の基準は sprites.ts の剣舞の表)。画面 px は絵の画素 × 0.2718 (剣舞の絵はどれもこの倍率で置かれる・実機の loadedBossSpriteArtPlacement で測った)。
//   当たる絵の剣先 (画面 px・足元から前, 上): 1発目 (152, 0)・2発目 (156, 0)。
import { ATTACKS } from './attacks'
import { PLAYER_DAMAGE } from './balance'
import { BOSS_DEFINITIONS } from './bosses'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { SMEAR_ARTS, preloadBladePetals, type ContactSmearArc } from './render'
import type { AttackDef } from './types'
import { BLADE_SMEAR_BASE, bladeDancerV6Smear, TENSE, withDissolves, type BladeTrailStyle } from './blade_dancer_art'

const BOSS_ID = 'blade-dancer'
export const BLADE_GAMMA_ID = 'bladeCrimson'
const G = (n: number) => `m1025_g_${n}`
const P8 = 'm1025_p4_8'              // 肩で息をする残心 (旋回突き b4 と同じ絵)
const SETTLE = 'm1020_waltz_s2'
const STAND = 'm1020_waltz_c6'

const TICK = 16
const ACTIVE_MS = 130
const DELAY_MS = 380                 // 1発目から爆ぜるまで
export const BLADE_GAMMA_GAPS_MS = [DELAY_MS - ACTIVE_MS - TICK]
const CHARGE_MS = 1500
const RECOVERY_MS = 1100

const TIP_FWD_PX = [152, 156] as const
const REACH_FIT_MARGIN_PX = 15
const REAR_PX = -100
// 2発目は剣先の床だけが爆ぜる (体の前には当たらない)。
const BURST_BACK_PX = 50
const BURST_FRONT_PX = 25
export const BLADE_GAMMA_STRIKES = [
  { localFromPx: REAR_PX, localToPx: TIP_FWD_PX[0] + REACH_FIT_MARGIN_PX },
  { localFromPx: TIP_FWD_PX[1] - BURST_BACK_PX, localToPx: TIP_FWD_PX[1] + BURST_FRONT_PX },
] as const
const STEP_IN_MS = 220
const STOP_BUFFER_PX = 8
const LUNGE_PX = 240

const CORE = '#ffd6d6'
const GLOW = '#c4141f'

export const BLADE_GAMMA_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SETTLE, hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.1, image: G(1), hold: true, ease: 'easeIn' },                 // 剣2本を縦に立てて刃を見せる (赤く染まる間)
  { phase: 'telegraph', at: 0.6, image: G(1), hold: true, offset: { y: -4 }, scale: { x: 0.98, y: 1.04 }, ease: 'easeInOut' }, // 10-28 伸び上がってから振りかぶる
  { phase: 'telegraph', at: 0.72, image: G(2), hold: true, offset: { x: -10 }, scale: { x: 0.98, y: 1.04 }, rot: -4, ease: 'easeOut' }, // 右後ろ高くへ振りかぶる
  { phase: 'telegraph', at: 0.9, image: G(2), hold: true, ...TENSE, ease: 'easeIn' },
  { phase: 'active', at: 0, image: G(3), hold: true, tag: 'contact', hitIndex: 0, offset: { x: 6 }, scale: { x: 1.05, y: 0.95 }, rot: 2 }, // 1発目: 踏み込んで足元の前まで斬り下ろす
  { phase: 'comboGap', at: 0, image: G(3), hold: true, tag: 'followThrough', hitIndex: 0, offset: { x: 8 }, rot: 3, ease: 'easeOut' },
  { phase: 'comboGap', at: 0.5, image: G(4), hold: true, hitIndex: 0, ease: 'easeOut' },     // 振り抜いたまま止まる
  { phase: 'active', at: 0, image: G(4), hold: true, tag: 'contact', hitIndex: 1 },         // 2発目: 剣先の床が遅れて爆ぜる
  // 10-28 締めは息をつく残心から、段を踏んで立ち姿へ。
  { phase: 'recovery', at: 0, image: G(4), hold: true, tag: 'followThrough' },
  { phase: 'recovery', at: 0.3, image: P8, hold: true, tag: 'recover', ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.46, image: 'm1020_v6_g2', hold: true, ease: 'easeOut', soft: true },   // 10-28 低い踏み込みから半ば立ち上がる
  { phase: 'recovery', at: 0.6, image: SETTLE, hold: true, ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.76, image: STAND, hold: true, ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.9, image: 'idle', hold: true },
], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })

// 1発目は頭の後ろ上から剣先 (152, 0) まで大きく斬り下ろす赤い弧。2発目は剣先 (156, 0) の短い弧から、赤い花びらと火花が上へ舞う。
export const BLADE_GAMMA_SMEAR: Record<string, ContactSmearArc> = {
  [G(3)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 23, anchorUpPx: 80, radiusPx: 152.3, angleFromRad: 2.0, angleToRad: -0.553, widthPx: 9,
    trailHeadStart: 0.35, coreColor: CORE, glowColor: GLOW },
  [G(4)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 114, anchorUpPx: 22, radiusPx: 48.0, angleFromRad: 0.4, angleToRad: -0.495,
    coreColor: CORE, glowColor: GLOW, sparks: true, petals: { spread: 'up', count: 18, seed: 25 } },
}

const DEF: AttackDef = {
  id: BLADE_GAMMA_ID,
  label: '遅れの一撃',
  kind: 'physical',
  chargeMs: CHARGE_MS, activeMs: ACTIVE_MS, recoveryMs: RECOVERY_MS,
  parryable: false, defense: 'evade', unblockable: true, auraHoldThroughCombo: true,
  damage: PLAYER_DAMAGE.standard, postureDamage: 30,
  comboHits: 2, comboGapMs: BLADE_GAMMA_GAPS_MS[0], comboGapsMs: [...BLADE_GAMMA_GAPS_MS],
  range: BLADE_GAMMA_STRIKES[0].localToPx, approachDistancePx: BLADE_GAMMA_STRIKES[0].localToPx,
  strike: { ...BLADE_GAMMA_STRIKES[0] },
  comboStages: BLADE_GAMMA_STRIKES.map(s => ({ strike: { ...s } })),
  lungeDistance: LUNGE_PX, gapCloseBuffer: STOP_BUFFER_PX,
  preActiveLungeMs: STEP_IN_MS, preActiveLungeDistance: LUNGE_PX,
  preActiveLungeStopGapPx: BLADE_GAMMA_STRIKES[0].localToPx - STOP_BUFFER_PX, preActiveLungeEase: 'smooth',
  // 2発目は振り抜いた所から動かない (剣先の床が爆ぜる)。
  comboLunge: { followHits: 0, followSpeed: 0, advanceSpeed: 0, plantFromHit: 0, gapOnly: true },
  sourceBossId: BOSS_ID,
}

let installed = false

/** 剣舞に「遅れの一撃」を足す (DEV ?bladeGamma=1)。最初から出る技の組に入れる。 */
export function useBladeDancerGamma(trail: BladeTrailStyle = 'afterimage'): void {
  if (!installed) {
    ATTACKS[BLADE_GAMMA_ID] = { ...DEF }
    const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
    def.baseAttackIds.push(BLADE_GAMMA_ID)
    def.unlockByExecutes?.[0]?.ids.push(BLADE_GAMMA_ID)
    installed = true
  }
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), [BLADE_GAMMA_ID]: BLADE_GAMMA_TIMELINE }
  const smear = bladeDancerV6Smear(trail, BLADE_GAMMA_SMEAR)
  // 爆ぜる2発目は帯にしない (花びらと火花のまま)。
  smear[G(4)] = BLADE_GAMMA_SMEAR[G(4)]!
  Object.assign(SMEAR_ARTS, smear)
  preloadBladePetals()
}

/** 外す (テストの後始末用)。 */
export function restoreBladeDancerGamma(): void {
  if (!installed) return
  delete ATTACKS[BLADE_GAMMA_ID]
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  def.baseAttackIds = def.baseAttackIds.filter(id => id !== BLADE_GAMMA_ID)
  const first = def.unlockByExecutes?.[0]
  if (first) first.ids = first.ids.filter(id => id !== BLADE_GAMMA_ID)
  delete BOSS_ATTACK_TIMELINES[BOSS_ID]?.[BLADE_GAMMA_ID]
  for (const k of Object.keys(BLADE_GAMMA_SMEAR)) delete SMEAR_ARTS[k]
  installed = false
}

export function parseBladeGamma(v: string | null): boolean {
  return v === '1'
}
